import apiClient from '@/utils/apiClient';
import { ADMIN_ENDPOINTS } from './constants';

// ─── Types ────────────────────────────────────────────────────────

export interface AdminMerchant {
    merchantId: string;
    businessName: string;
    businessEmail: string;
    status: string;
    createdAt: string;
    stores: AdminStore[];
    storeCount: number;
    features?: Record<string, boolean>;
}

export type MerchantStatus = 'active' | 'trialing' | 'suspended' | 'inactive';

export interface AdminStore {
    storeId: string;
    storeName: string;
    storeUrl: string;
    status: string;
    platform: string;
}

export interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface AuditEntry {
    auditId: string;
    adminUserId: string;
    adminEmail: string;
    targetMerchantId: string;
    targetMerchantName: string;
    targetStoreId: string | null;
    action: string;
    resourceType: string | null;
    resourceId: string | null;
    requestBody: Record<string, any>;
    ipAddress: string;
    createdAt: string;
}

export interface AdminUser {
    userId: string;
    email: string;
    status: 'active' | 'inactive';
    lastLoginAt: string | null;
    createdAt: string;
}

export interface Device {
    deviceId: string;
    userId: string;
    email?: string;
    label: string;
    status: 'active' | 'pending' | 'revoked';
    lastUsedAt: string | null;
    requestedAt: string | null;
    createdAt: string;
}

// ─── API Functions ────────────────────────────────────────────────

export const adminApi = {
    listMerchants: async (params?: { page?: number; limit?: number; search?: string }) => {
        const response = await apiClient.get<{ data: { merchants: AdminMerchant[]; pagination: Pagination } }>(
            ADMIN_ENDPOINTS.MERCHANTS,
            { params }
        );
        return response.data.data;
    },

    getMerchant: async (merchantId: string) => {
        const response = await apiClient.get<{ data: AdminMerchant }>(
            ADMIN_ENDPOINTS.MERCHANT_DETAIL(merchantId)
        );
        return response.data.data;
    },

    impersonate: async (body: { merchantId: string; storeId: string }) => {
        const response = await apiClient.post<{ data: { token: string; merchantId: string; storeId: string; merchantName: string; isImpersonating: boolean } }>(
            ADMIN_ENDPOINTS.IMPERSONATE,
            body
        );
        return response.data.data;
    },

    stopImpersonation: async () => {
        const response = await apiClient.post<{ data: { token: string; isImpersonating: boolean } }>(
            ADMIN_ENDPOINTS.STOP_IMPERSONATION
        );
        return response.data.data;
    },

    getAuditLog: async (params?: {
        page?: number;
        limit?: number;
        adminUserId?: string;
        targetMerchantId?: string;
        startDate?: string;
        endDate?: string;
    }) => {
        const response = await apiClient.get<{ data: { entries: AuditEntry[]; pagination: Pagination } }>(
            ADMIN_ENDPOINTS.AUDIT_LOG,
            { params }
        );
        return response.data.data;
    },

    verifyDevice: async (fingerprint: string) => {
        const response = await apiClient.post<{ data: { verified: boolean; pendingApproval: boolean; revoked: boolean } }>(
            ADMIN_ENDPOINTS.VERIFY_DEVICE,
            { fingerprint }
        );
        return response.data.data;
    },

    listAdminUsers: async () => {
        const response = await apiClient.get<{ data: { users: AdminUser[]; cap: number } }>(
            ADMIN_ENDPOINTS.USERS
        );
        return response.data.data;
    },

    createAdminUser: async (body: { email: string; password?: string }) => {
        const response = await apiClient.post<{ data: { userId: string; email: string } }>(
            ADMIN_ENDPOINTS.USERS,
            body
        );
        return response.data.data;
    },

    updateAdminUserStatus: async (userId: string, status: 'active' | 'inactive') => {
        const response = await apiClient.patch<{ data: AdminUser }>(
            `${ADMIN_ENDPOINTS.USERS}/${userId}/status`,
            { status }
        );
        return response.data.data;
    },

    listDevices: async () => {
        const response = await apiClient.get<{ data: { devices: Device[] } }>(
            ADMIN_ENDPOINTS.DEVICES
        );
        return response.data.data;
    },

    listPendingDevices: async () => {
        const response = await apiClient.get<{ data: { devices: Device[] } }>(
            ADMIN_ENDPOINTS.DEVICES_PENDING
        );
        return response.data.data;
    },

    approveDevice: async (deviceId: string) => {
        const response = await apiClient.post<{ data: Device }>(
            `${ADMIN_ENDPOINTS.DEVICES}/${deviceId}/approve`
        );
        return response.data.data;
    },

    revokeDevice: async (deviceId: string) => {
        const response = await apiClient.post<{ data: Device }>(
            `${ADMIN_ENDPOINTS.DEVICES}/${deviceId}/revoke`
        );
        return response.data.data;
    },

    toggleMerchantFeature: async (merchantId: string, featureKey: string, enabled: boolean) => {
        const response = await apiClient.patch<{ data: { merchantId: string; features: Record<string, boolean> } }>(
            ADMIN_ENDPOINTS.MERCHANT_FEATURES(merchantId),
            { featureKey, enabled }
        );
        return response.data.data;
    },

    updateMerchantStatus: async (merchantId: string, status: MerchantStatus) => {
        const response = await apiClient.patch<{ data: AdminMerchant }>(
            ADMIN_ENDPOINTS.MERCHANT_STATUS(merchantId),
            { status }
        );
        return response.data.data;
    },
};
